import { defineComponent as _defineComponent } from 'vue'
import Dropdown from "primevue/dropdown"
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";
import {reactive, computed} from "vue";
import {
  UserCategoryGroup
} from "@/backoffice/components/pages/settings/usercategory/user-category";
import {getCurrentInstance, ref} from "vue";
import VueI18n from "vue-i18n";
import Skeleton from "@/components/decorations/Skeleton.vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import {useCategories, useCurrentUserCategory} from "capitalist-shared/src/profile/categories";



export default /*#__PURE__*/_defineComponent({
  __name: 'FrontofficeUserCategoryInlineSelect',
  setup(__props) {

const categoriesData = useCategories();
const whoamiData = useCurrentUserCategory();

const sortingOrder = [
  UserCategoryGroup.individual,
  UserCategoryGroup.business,
  UserCategoryGroup.business_service,
  UserCategoryGroup.business_advertising_network,
  UserCategoryGroup.business_affiliate_network,
  UserCategoryGroup.business_webmaster_service,
  UserCategoryGroup.business_marketing_agency,
];
const locale = computed(() => {
  return getCurrentInstance()?.proxy?.$i18n.locale || 'ru';
})

const ProfileUsertypeEnum = {
  Individual: 'INDIVIDUAL',
  Business: 'LEGAL',
}

const preferredUserType = ref(ProfileUsertypeEnum.Individual);

const filterUserType = computed(() => {
  if (!whoamiData.data?.value?.profile)
    return null;
  return whoamiData.data?.value?.profile?.isVerified
    ? whoamiData.data?.value?.profile?.type : preferredUserType.value;
})

const categories = computed(() => {
  return categoriesData.data?.value || [];
})

const categoryOptions = computed(() => {
  const $i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  const result = categories.value.filter((val) => {
    if (val.id == whoamiData.data?.value?.profile?.categoryId) return true;
    if (!val.visible) return false;
    if (filterUserType.value) {
      return val.usertype == filterUserType.value;
    }
    return true;
  }).sort((a, b) => {
    const key = locale.value == 'ru' ? 'name' : 'name_en';
    return a[key].localeCompare(b[key]);
  }).sort((a, b) => {
    return - sortingOrder.indexOf(a.group) + sortingOrder.indexOf(b.group);
  }).sort((a, b) => {
    return a.other ? 1 : -1;
  }).map((val) => {
    return {...val,
      lname: $i18n.locale == 'ru' ? val.name : val.name_en,
      disabled: false};
  })

  sortingOrder.forEach((group) => {
    const gIndex = result.findIndex((val) => {
      return val.group == group;
    })

    if (gIndex >= 0)
    {
      result.splice(gIndex, 0, {
        id: group,
        name: $i18n.t('category_name.' + group) as string,
        name_en: $i18n.t('category_name.' + group) as string,
        lname: $i18n.t('category_name.' + group) as string,
        group: 'root',
        usertype: result[gIndex].usertype,
        visible: true,
        other: false,
        disabled: true,
      });
    }
  })


  const oIndex = result.findIndex((val) => {
    return val.other;
  })

  if (oIndex >= 0)
  {
    result.splice(oIndex, 0, {
      id: 'other',
      name: $i18n.t('Другое') as string,
      name_en: $i18n.t('Другое') as string,
      lname: $i18n.t('Другое') as string,
      group: 'root',
      usertype: result[oIndex].usertype,
      visible: true,
      other: false,
      disabled: true,
    });
  }

  // filter disabled
  return result.filter((val) => {
    return !val.disabled;
  })

  return result;
})

class UserCategoryUpdateModel extends WebFormModel {
  update(categoryId) {
    this.errors.clear();
    return httpAxiosApi.post('/webapi/profile/update/category', {
      categoryId: categoryId
    }).then((response) => {
      return response.data;
    }).catch((error) => {
      this.checkAxiosError(error);
      throw error;
    })
  }
}

const model = reactive(new UserCategoryUpdateModel());
const updating = ref(false);

const startEditMode = () => {
  preferredUserType.value = whoamiData.data?.value?.profile?.type;
  editMode.value = true;
}

const onUpdateCategory = (val) => {
  // console.log('onUpdateCategory', val, whoamiData.data?.profile?.categoryId);
  if (val === whoamiData.data?.value?.profile?.categoryId) {
    editMode.value = false;
    return;
  }

  updating.value = true;
  model.update(val).then((data) => {
    whoamiData.mutate(() => ({
      ...whoamiData.data?.value,
      ...data,
    }));
    updating.value = false;
    editMode.value = false;
  }).catch(() => {
    updating.value = false;
  });
}

const category = computed({
  get: () => {
    return computedCategory.value?.id;
  },
  set: (val) => {
    updating.value = true;
    model.update(val).then((data) => {
      whoamiData.mutate(() => ({
        ...whoamiData.data.value,
        ...data,
      }));
      updating.value = false;
      editMode.value = false;
    }).catch(() => {
      updating.value = false;
    });

    whoamiData.mutate(() => ({
      ...whoamiData.data?.value,
      profile: {
        ...whoamiData.data?.value.profile,
        categoryId: val
      }
    }));
  }
})

const editMode = ref(false)

const anyLoading = computed(() => {
  return categoriesData.loading?.value || whoamiData.loading?.value;
})

const anyError = computed(() => {
  return categoriesData.error?.value || whoamiData.error?.value;
})

const allData = computed(() => {
  return categoriesData.data?.value && whoamiData.data.value;
})

const computedCategory = computed(() => {
  if (!allData.value || !whoamiData.data?.value?.profile?.categoryId)
    return null;

  const result = categories.value.find((val) => {
    return val.id == whoamiData.data?.value?.profile?.categoryId;
  });
  const $i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return result ? {
    ...result,
    lname: $i18n.locale == 'ru' ? result.name : result.name_en,
  } : null
})

const preferredUserTypeOptions = computed(() => {
  const $i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return [
    {label: $i18n.t('Частное лицо'), value: ProfileUsertypeEnum.Individual},
    {label: $i18n.t('Бизнес'), value: ProfileUsertypeEnum.Business},
  ]
})

const getTypeOptionByValue = (value) => {
  return preferredUserTypeOptions.value.find((val) => {
    return val.value == value;
  })
}

return { __sfc: true,categoriesData, whoamiData, sortingOrder, locale, ProfileUsertypeEnum, preferredUserType, filterUserType, categories, categoryOptions, UserCategoryUpdateModel, model, updating, startEditMode, onUpdateCategory, category, editMode, anyLoading, anyError, allData, computedCategory, preferredUserTypeOptions, getTypeOptionByValue, Dropdown, Skeleton }
}

})